'use client';

import { Link, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useControls } from '@/features/controls/api/use-controls';
import { DEFAULT_CONTROL_FILTERS } from '@/features/controls/types';
import type { Control, ControlFilters } from '@/features/controls/types';
import { useMembers } from '@/features/team/api/use-members';
import type { OrganizationMember } from '@/features/team/types';
import {
  useAwarenessCampaigns,
  useCreateAwarenessCampaign,
} from '@/features/workforce-readiness/api/use-awareness-campaigns';
import {
  type AwarenessCampaign,
  type CampaignStatus,
  type ParticipationStatus,
} from '@/features/workforce-readiness/data';
import {
  buildAwarenessCampaignInput,
  mapAwarenessCampaignFromApi,
} from '@/features/workforce-readiness/mappers';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

type CampaignStatusFilter = 'all' | CampaignStatus;

interface CampaignParticipantFormValue {
  participationStatus: ParticipationStatus;
  result: string;
}

interface CampaignFormState {
  campaignName: string;
  campaignType: string;
  description: string;
  status: CampaignStatus;
  startDate: string;
  endDate: string;
  selectedParticipantIds: string[];
  participantState: Record<string, CampaignParticipantFormValue>;
  selectedControlIds: string[];
}

const EMPTY_FORM_STATE: CampaignFormState = {
  campaignName: '',
  campaignType: '',
  description: '',
  status: 'planned',
  startDate: '',
  endDate: '',
  selectedParticipantIds: [],
  participantState: {},
  selectedControlIds: [],
};

export const Route = createFileRoute('/_authenticated/workforce-readiness/awareness-campaigns')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.workforceReadinessAwarenessCampaigns'),
  }),
  component: AwarenessCampaignsPage,
});

function AwarenessCampaignsPage() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  if (pathname !== '/workforce-readiness/awareness-campaigns') {
    return <Outlet />;
  }

  return <AwarenessCampaignsContent />;
}

function AwarenessCampaignsContent() {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<CampaignFormState>(EMPTY_FORM_STATE);
  const [formError, setFormError] = useState<string | null>(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [controlSearch, setControlSearch] = useState('');

  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const campaignsQuery = useAwarenessCampaigns({
    status: statusFilter === 'all' ? undefined : statusFilter,
    search: search.trim() || undefined,
  });
  const createCampaignMutation = useCreateAwarenessCampaign();
  const campaigns = useMemo(
    () => campaignsQuery.data?.items.map(mapAwarenessCampaignFromApi) ?? [],
    [campaignsQuery.data?.items]
  );
  const activeOrg = organizations.find((org) => org.isActive);
  const canManageAssignments = Boolean(activeOrg?.id);

  const membersQueryOptions = useMemo(
    () => ({
      orgId: activeOrg?.id ?? '',
      page: 1,
      pageSize: 300,
      sortBy: 'name' as const,
      sortOrder: 'asc' as const,
      enabled: Boolean(activeOrg?.id),
    }),
    [activeOrg?.id]
  );

  const { data: membersData, isLoading: membersLoading } = useMembers(membersQueryOptions);
  const availableMembers = useMemo(() => membersData?.members ?? [], [membersData?.members]);

  const controlsFilters = useMemo<ControlFilters>(
    () => ({
      ...DEFAULT_CONTROL_FILTERS,
      sortBy: 'title',
      sortOrder: 'asc',
    }),
    []
  );

  const { controls: availableControls, isLoading: controlsLoading } = useControls({
    filters: controlsFilters,
    page: 1,
    pageSize: 300,
    enabled: Boolean(activeOrg?.id),
  });

  const membersByUserId = useMemo(
    () => new Map(availableMembers.map((member) => [member.userId, member])),
    [availableMembers]
  );

  const controlsById = useMemo(
    () => new Map(availableControls.map((control) => [control.id, control])),
    [availableControls]
  );

  const campaignTypeOptions = useMemo(() => {
    const uniqueTypes = new Set(campaigns.map((campaign) => campaign.campaignType));
    const currentFormType = formState.campaignType.trim();

    if (currentFormType) {
      uniqueTypes.add(currentFormType);
    }

    return ['all', ...Array.from(uniqueTypes)];
  }, [campaigns, formState.campaignType]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      const matchesType = typeFilter === 'all' || campaign.campaignType === typeFilter;

      return matchesType;
    });
  }, [campaigns, typeFilter]);

  const filteredMembersForDialog = useMemo(() => {
    const query = participantSearch.trim().toLowerCase();
    if (!query) {
      return availableMembers;
    }

    return availableMembers.filter((member) => {
      const displayName = getMemberDisplayName(member).toLowerCase();
      const email = member.email.toLowerCase();
      return displayName.includes(query) || email.includes(query);
    });
  }, [availableMembers, participantSearch]);

  const filteredControlsForDialog = useMemo(() => {
    const query = controlSearch.trim().toLowerCase();
    if (!query) {
      return availableControls;
    }

    return availableControls.filter((control) => {
      const title = control.title.toLowerCase();
      const code = control.controlCode.toLowerCase();
      return title.includes(query) || code.includes(query);
    });
  }, [availableControls, controlSearch]);

  const openCreateDialog = () => {
    setFormState(EMPTY_FORM_STATE);
    setParticipantSearch('');
    setControlSearch('');
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setFormState(EMPTY_FORM_STATE);
    setFormError(null);
    setParticipantSearch('');
    setControlSearch('');
  };

  const upsertParticipantSelection = (userId: string, checked: boolean) => {
    setFormState((previousState) => {
      const selectedParticipantIds = checked
        ? uniqueValues([...previousState.selectedParticipantIds, userId])
        : previousState.selectedParticipantIds.filter((id) => id !== userId);

      if (checked) {
        const participantState = {
          ...previousState.participantState,
        };
        participantState[userId] ??= {
          participationStatus: 'pending',
          result: 'Pending',
        };

        return {
          ...previousState,
          selectedParticipantIds,
          participantState,
        };
      }

      const participantState = Object.fromEntries(
        Object.entries(previousState.participantState).filter(([id]) => id !== userId)
      ) as Record<string, CampaignParticipantFormValue>;

      return {
        ...previousState,
        selectedParticipantIds,
        participantState,
      };
    });
  };

  const upsertControlSelection = (controlId: string, checked: boolean) => {
    setFormState((previousState) => {
      const selectedControlIds = checked
        ? uniqueValues([...previousState.selectedControlIds, controlId])
        : previousState.selectedControlIds.filter((id) => id !== controlId);

      return {
        ...previousState,
        selectedControlIds,
      };
    });
  };

  const updateParticipantState = (userId: string, patch: Partial<CampaignParticipantFormValue>) => {
    setFormState((previousState) => {
      const currentValue = previousState.participantState[userId] ?? {
        participationStatus: 'pending',
        result: 'Pending',
      };

      return {
        ...previousState,
        participantState: {
          ...previousState.participantState,
          [userId]: {
            ...currentValue,
            ...patch,
          },
        },
      };
    });
  };

  const saveCampaign = async () => {
    const normalizedName = formState.campaignName.trim();
    const normalizedType = formState.campaignType.trim();

    if (!normalizedName) {
      setFormError(t('workforceReadiness.awareness.validationNameRequired'));
      return;
    }

    if (!normalizedType) {
      setFormError(t('workforceReadiness.awareness.validationTypeRequired'));
      return;
    }

    if (!formState.startDate || !formState.endDate) {
      setFormError(t('workforceReadiness.awareness.validationDatesRequired'));
      return;
    }

    if (formState.endDate < formState.startDate) {
      setFormError(t('workforceReadiness.awareness.validationEndAfterStart'));
      return;
    }

    setFormError(null);
    const nextCampaign: AwarenessCampaign = {
      id: '',
      campaignName: normalizedName,
      campaignType: normalizedType,
      description: formState.description.trim(),
      status: formState.status,
      startDate: formState.startDate,
      endDate: formState.endDate,
      participants: formState.selectedParticipantIds.map((userId) => {
        const participant = formState.participantState[userId] ?? {
          participationStatus: 'pending' as const,
          result: 'Pending',
        };

        return {
          userId,
          participationStatus: participant.participationStatus,
          result: participant.result.trim() || 'Pending',
        };
      }),
      linkedControlIds: formState.selectedControlIds,
      scheduleItems: [],
      evidenceItems: [],
      resourceLinks: [],
    };

    try {
      await createCampaignMutation.mutateAsync(buildAwarenessCampaignInput(nextCampaign));
      closeDialog();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : t('workforceReadiness.awareness.failedToCreateCampaign'));
    }
  };

  if (orgsLoading || (campaignsQuery.isLoading && campaigns.length === 0)) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-[1300px]">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-56 rounded bg-muted" />
            <div className="h-10 rounded bg-muted" />
            <div className="h-80 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (campaignsQuery.isError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-[1300px]">
            <Card>
              <CardContent className="py-6 text-sm text-destructive">
                {campaignsQuery.error.message || t('workforceReadiness.awareness.failedToLoadCampaigns')}
              </CardContent>
            </Card>
          </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-[1300px] space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('workforceReadiness.awareness.pageTitle')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('workforceReadiness.awareness.pageDescription')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to="/settings/team">{t('workforceReadiness.awareness.users')}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/audit/evidence-library">{t('workforceReadiness.awareness.evidence')}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/controls">{t('workforceReadiness.awareness.complianceControls')}</Link>
            </Button>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              {t('workforceReadiness.awareness.addCampaign')}
            </Button>
          </div>
        </header>

        {!canManageAssignments ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col gap-2 py-4 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-muted-foreground">
                {t('workforceReadiness.awareness.noOrgNotice')}
              </p>
              <Button variant="outline" asChild>
                <Link to="/settings/organizations">{t('workforceReadiness.awareness.goToOrganizations')}</Link>
              </Button>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('workforceReadiness.awareness.filters')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 lg:grid-cols-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                  }}
                  placeholder={t('workforceReadiness.awareness.searchPlaceholder')}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value as CampaignStatusFilter);
                }}
                options={[
                  { value: 'all', label: t('workforceReadiness.awareness.allStatuses') },
                  { value: 'planned', label: t('workforceReadiness.awareness.planned') },
                  { value: 'active', label: t('workforceReadiness.awareness.active') },
                  { value: 'completed', label: t('workforceReadiness.awareness.completed') },
                ]}
              />
              <Select
                value={typeFilter}
                onChange={(value) => {
                  setTypeFilter(value);
                }}
                options={campaignTypeOptions.map((type) => ({
                  value: type,
                  label: type === 'all' ? t('workforceReadiness.awareness.allCampaignTypes') : type,
                }))}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('workforceReadiness.awareness.campaignName')}</TableHead>
                    <TableHead>{t('workforceReadiness.awareness.campaignType')}</TableHead>
                    <TableHead>{t('workforceReadiness.awareness.status')}</TableHead>
                    <TableHead>{t('workforceReadiness.awareness.startDate')}</TableHead>
                    <TableHead>{t('workforceReadiness.awareness.endDate')}</TableHead>
                    <TableHead>{t('workforceReadiness.awareness.participants')}</TableHead>
                    <TableHead>{t('workforceReadiness.awareness.participationResults')}</TableHead>
                    <TableHead>{t('workforceReadiness.awareness.evidenceCol')}</TableHead>
                    <TableHead>{t('workforceReadiness.awareness.complianceControlsCol')}</TableHead>
                    <TableHead className="w-[180px]">{t('workforceReadiness.awareness.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCampaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="py-6 text-center text-muted-foreground">
                        {t('workforceReadiness.awareness.noMatchingCampaigns')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCampaigns.map((campaign) => (
                      <TableRow key={campaign.id} className="align-top">
                        <TableCell className="min-w-[250px]">
                          <Link
                            to="/workforce-readiness/awareness-campaigns/$campaignId"
                            params={{ campaignId: campaign.id }}
                            className="font-medium text-primary hover:underline"
                          >
                            {campaign.campaignName}
                          </Link>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {campaign.description}
                          </p>
                        </TableCell>
                        <TableCell>{campaign.campaignType}</TableCell>
                        <TableCell>
                          <Badge variant={getCampaignStatusBadgeVariant(campaign.status)}>
                            {toLabel(campaign.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(campaign.startDate)}</TableCell>
                        <TableCell>{formatDate(campaign.endDate)}</TableCell>
                        <TableCell className="min-w-[220px]">
                          {campaign.participants.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              {t('workforceReadiness.awareness.noParticipantsAssigned')}
                            </p>
                          ) : (
                            <div className="space-y-1.5">
                              {campaign.participants.map((participant) => {
                                const member = membersByUserId.get(participant.userId);
                                const displayName = getMemberDisplayName(
                                  member,
                                  participant.userId
                                );

                                if (member) {
                                  return (
                                    <Link
                                      key={`${campaign.id}-${participant.userId}`}
                                      to="/settings/team/members/$userId"
                                      params={{ userId: participant.userId }}
                                      className="block text-sm font-medium text-primary hover:underline"
                                    >
                                      {displayName}
                                    </Link>
                                  );
                                }

                                return (
                                  <p
                                    key={`${campaign.id}-${participant.userId}`}
                                    className="text-sm text-muted-foreground"
                                  >
                                    {displayName}
                                  </p>
                                );
                              })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[300px]">
                          {campaign.participants.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              {t('workforceReadiness.awareness.noParticipationData')}
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {campaign.participants.map((participant) => {
                                const member = membersByUserId.get(participant.userId);
                                const displayName = getMemberDisplayName(
                                  member,
                                  participant.userId
                                );

                                return (
                                  <div
                                    key={`${campaign.id}-participant-${participant.userId}`}
                                    className="rounded-md border p-2 text-xs"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      {member ? (
                                        <Link
                                          to="/settings/team/members/$userId"
                                          params={{ userId: participant.userId }}
                                          className="font-medium text-primary hover:underline"
                                        >
                                          {displayName}
                                        </Link>
                                      ) : (
                                        <span className="font-medium text-muted-foreground">
                                          {displayName}
                                        </span>
                                      )}
                                      <Badge
                                        variant={getParticipationStatusBadgeVariant(
                                          participant.participationStatus
                                        )}
                                      >
                                        {toLabel(participant.participationStatus)}
                                      </Badge>
                                    </div>
                                    <p className="mt-1 text-muted-foreground">
                                      {t('workforceReadiness.awareness.result', { result: participant.result })}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[180px]">
                          {(campaign.evidenceCount ?? 0) > 0 ? (
                            <Link
                              to="/workforce-readiness/awareness-campaigns/$campaignId"
                              params={{ campaignId: campaign.id }}
                              className="text-sm text-primary hover:underline"
                            >
                              {t('workforceReadiness.awareness.linkedEvidenceCount', { count: campaign.evidenceCount ?? 0 })}
                            </Link>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              {t('workforceReadiness.awareness.noEvidenceLinked')}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[220px]">
                          {campaign.linkedControlIds.length === 0 ? (
                            <p className="text-sm text-muted-foreground">{t('workforceReadiness.awareness.noControlsLinked')}</p>
                          ) : (
                            <div className="space-y-1.5">
                              {campaign.linkedControlIds.map((controlId) => {
                                const control = controlsById.get(controlId);
                                const label = getControlLabel(control, controlId);

                                return (
                                  <Link
                                    key={`${campaign.id}-${controlId}`}
                                    to="/controls/$controlId"
                                    params={{ controlId }}
                                    className="block text-sm text-primary hover:underline"
                                  >
                                    {label}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" asChild>
                              <Link
                                to="/workforce-readiness/awareness-campaigns/$campaignId"
                                params={{ campaignId: campaign.id }}
                              >
                                {t('workforceReadiness.awareness.open')}
                              </Link>
                            </Button>
                            <Button variant="outline" size="sm" asChild>
                              <Link
                                to="/workforce-readiness/awareness-campaigns/$campaignId"
                                params={{ campaignId: campaign.id }}
                              >
                                {t('workforceReadiness.awareness.edit')}
                              </Link>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          } else {
            setDialogOpen(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t('workforceReadiness.awareness.addCampaignDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('workforceReadiness.awareness.addCampaignDialogDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[70vh] overflow-auto pr-1">
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.awareness.campaignNameLabel')}</p>
                  <Input
                    value={formState.campaignName}
                    onChange={(event) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        campaignName: event.target.value,
                      }));
                    }}
                    placeholder={t('workforceReadiness.awareness.campaignNamePlaceholder')}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.awareness.campaignTypeLabel')}</p>
                  <Input
                    value={formState.campaignType}
                    onChange={(event) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        campaignType: event.target.value,
                      }));
                    }}
                    placeholder={t('workforceReadiness.awareness.campaignTypePlaceholder')}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.awareness.statusLabel')}</p>
                  <Select
                    value={formState.status}
                    onChange={(value) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        status: value as CampaignStatus,
                      }));
                    }}
                    options={[
                      { value: 'planned', label: t('workforceReadiness.awareness.planned') },
                      { value: 'active', label: t('workforceReadiness.awareness.active') },
                      { value: 'completed', label: t('workforceReadiness.awareness.completed') },
                    ]}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.awareness.evidence')}</p>
                  <p className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
                    {t('workforceReadiness.awareness.linkEvidenceAfterCreation')}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.awareness.startDateLabel')}</p>
                  <Input
                    type="date"
                    value={formState.startDate}
                    onChange={(event) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        startDate: event.target.value,
                      }));
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('workforceReadiness.awareness.endDateLabel')}</p>
                  <Input
                    type="date"
                    value={formState.endDate}
                    onChange={(event) => {
                      setFormState((previousState) => ({
                        ...previousState,
                        endDate: event.target.value,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-sm font-medium">{t('workforceReadiness.awareness.descriptionLabel')}</p>
                <Textarea
                  value={formState.description}
                  onChange={(event) => {
                    setFormState((previousState) => ({
                      ...previousState,
                      description: event.target.value,
                    }));
                  }}
                  placeholder={t('workforceReadiness.awareness.descriptionPlaceholder')}
                  rows={3}
                />
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t('workforceReadiness.awareness.participantsCard')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input
                      value={participantSearch}
                      onChange={(event) => {
                        setParticipantSearch(event.target.value);
                      }}
                      placeholder={t('workforceReadiness.awareness.searchTeamMembers')}
                      disabled={!canManageAssignments}
                    />
                    <div className="max-h-56 space-y-2 overflow-auto rounded-md border p-2">
                      {!canManageAssignments ? (
                        <p className="text-sm text-muted-foreground">
                          {t('workforceReadiness.awareness.selectOrgToLoadTeam')}
                        </p>
                      ) : membersLoading ? (
                        <p className="text-sm text-muted-foreground">{t('workforceReadiness.awareness.loadingParticipants')}</p>
                      ) : filteredMembersForDialog.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('workforceReadiness.awareness.noMatchingTeamMembers')}</p>
                      ) : (
                        filteredMembersForDialog.map((member) => {
                          const isChecked = formState.selectedParticipantIds.includes(
                            member.userId
                          );

                          return (
                            <label
                              key={member.userId}
                              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted"
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  upsertParticipantSelection(member.userId, checked === true);
                                }}
                              />
                              <span className="text-sm">
                                {getMemberDisplayName(member)}
                                <span className="ml-1 text-xs text-muted-foreground">
                                  ({member.email})
                                </span>
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t('workforceReadiness.awareness.linkedControlsCard')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input
                      value={controlSearch}
                      onChange={(event) => {
                        setControlSearch(event.target.value);
                      }}
                      placeholder={t('workforceReadiness.awareness.searchControls')}
                      disabled={!canManageAssignments}
                    />
                    <div className="max-h-56 space-y-2 overflow-auto rounded-md border p-2">
                      {!canManageAssignments ? (
                        <p className="text-sm text-muted-foreground">
                          {t('workforceReadiness.awareness.selectOrgToLoadControls')}
                        </p>
                      ) : controlsLoading ? (
                        <p className="text-sm text-muted-foreground">{t('workforceReadiness.awareness.loadingControls')}</p>
                      ) : filteredControlsForDialog.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('workforceReadiness.awareness.noMatchingControls')}</p>
                      ) : (
                        filteredControlsForDialog.map((control) => {
                          const isChecked = formState.selectedControlIds.includes(control.id);

                          return (
                            <label
                              key={control.id}
                              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted"
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  upsertControlSelection(control.id, checked === true);
                                }}
                              />
                              <span className="text-sm">
                                {getControlLabel(control, control.id)}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('workforceReadiness.awareness.participationStatusCard')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {formState.selectedParticipantIds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('workforceReadiness.awareness.selectParticipantsNotice')}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {formState.selectedParticipantIds.map((userId) => {
                        const member = membersByUserId.get(userId);
                        const displayName = getMemberDisplayName(member, userId);
                        const participant = formState.participantState[userId] ?? {
                          participationStatus: 'pending' as const,
                          result: 'Pending',
                        };

                        return (
                          <div key={userId} className="rounded-md border p-3">
                            <p className="mb-2 text-sm font-medium">{displayName}</p>
                            <div className="grid gap-2 md:grid-cols-2">
                              <Select
                                value={participant.participationStatus}
                                onChange={(value) => {
                                  updateParticipantState(userId, {
                                    participationStatus: value as ParticipationStatus,
                                  });
                                }}
                                options={[
                                  { value: 'pending', label: t('workforceReadiness.awareness.pending') },
                                  { value: 'participated', label: t('workforceReadiness.awareness.participated') },
                                  { value: 'failed', label: t('workforceReadiness.awareness.failed') },
                                ]}
                              />
                              <Input
                                value={participant.result}
                                onChange={(event) => {
                                  updateParticipantState(userId, {
                                    result: event.target.value,
                                  });
                                }}
                                placeholder={t('workforceReadiness.awareness.resultPlaceholder')}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {formError ? (
                <p className="text-sm font-medium text-destructive">{formError}</p>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={createCampaignMutation.isPending}>
              {t('workforceReadiness.awareness.cancel')}
            </Button>
            <Button
              onClick={() => {
                void saveCampaign();
              }}
              disabled={createCampaignMutation.isPending}
            >
              {createCampaignMutation.isPending
                ? t('workforceReadiness.awareness.saving')
                : t('workforceReadiness.awareness.createCampaign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function uniqueValues(values: string[]): string[] {
  return Array.from(new Set(values));
}

function getMemberDisplayName(
  member: OrganizationMember | undefined,
  fallbackUserId?: string
): string {
  if (!member) {
    return fallbackUserId ?? 'Unknown user';
  }

  if (member.displayName) {
    return member.displayName;
  }

  const firstName = member.firstName?.trim() ?? '';
  const lastName = member.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return member.email;
}

function getControlLabel(control: Control | undefined, fallbackControlId: string): string {
  if (!control) {
    return fallbackControlId;
  }

  return `${control.controlCode} - ${control.title}`;
}

function getCampaignStatusBadgeVariant(
  status: CampaignStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'active') {
    return 'default';
  }
  if (status === 'completed') {
    return 'secondary';
  }
  return 'outline';
}

function getParticipationStatusBadgeVariant(
  status: ParticipationStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'participated') {
    return 'secondary';
  }
  if (status === 'failed') {
    return 'destructive';
  }
  return 'outline';
}

function toLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString();
}
